import React, { useState } from "react";
import { MediaFeature } from "../features/MediaFeature";

import { CheckIcon, CursorClickIcon, XIcon } from "@heroicons/react/solid";
import cx from "classnames";
import { OutboundLink } from "gatsby-plugin-google-gtag";
import IOVideoMP4 from "../../../images/pricing/IOVideo.mp4";
import IOVideoWEBM from "../../../images/pricing/IOVideo.webm";
import TTSVideoMP4 from "../../../images/pricing/tts.mp4";
import TTSVideoWEBM from "../../../images/pricing/tts.webm";
import { SmartLearningFeature } from "../features/SmartLearningFeature";
import {
  PricingMode,
  PricingTier,
  PricingTierData,
  PRICING_TIERS,
  PRO_PRICING_PLANS,
} from "./pricedata";
import { useMembershipPrice } from "../../../hooks/useMembershipPrice";

const PricingHero: React.FC = () => (
  <div className="mb-4 text-center">
    <h1 className="mb-4 text-3xl font-bold text-transparent xs:text-4xl sm:text-5xl font-heading bg-clip-text bg-gradient-to-r from-blue-600 via-purple-500 to-red-400">
      Upgrade to Zorbi Pro
    </h1>
    <h3 className="max-w-3xl mx-auto mb-4 text-xl font-bold text-gray-700 sm:text-3xl ">
      For learners that want hyperefficient learning
    </h3>
  </div>
);

const IOVideos: React.FC = () => {
  return (
    <video
      autoPlay
      playsInline
      muted
      loop
      preload="auto"
      className="max-w-sm sm:max-w-2xl rounded-DEFAULT"
    >
      <source src={IOVideoWEBM} type="video/webm" />
      <source src={IOVideoMP4} type="video/mp4" />
    </video>
  );
};

export const ImageClozeFeature: React.FC = () => (
  <MediaFeature
    header={
      <>
        <div className="flex flex-row">
          <span>The fastest way to</span>
        </div>
        <span className="text-purple-600">learn from diagrams</span>
        <br />
      </>
    }
    body={
      <>
        Zorbi makes it easy to learn diagrams by letting you draw, label, and
        hide areas of images. Hidden sections are grouped by colours -
        we&apos;ll test you on each section separately.
        <br />
        <br />
        {/* <HeroActionButton
          href={
            "https://zorbiapp.notion.site/Learning-Anatomy-with-Zorbi-6f335e7aeb1940a4852b9645b7189d54"
          }
        >
          Learn more.
        </HeroActionButton> */}
        {/* <br />
        <br /> */}
      </>
    }
    media={<IOVideos />}
    mediaSide={"left"}
    disableMediaShadow={true}
  />
);

const TTSVideos: React.FC = () => {
  return (
    <video
      autoPlay
      playsInline
      muted
      loop
      preload="auto"
      className="max-w-sm sm:max-w-xl rounded-DEFAULT"
    >
      <source src={TTSVideoWEBM} type="video/webm" />
      <source src={TTSVideoMP4} type="video/mp4" />
    </video>
  );
};

const TTSFeature: React.FC = () => (
  <MediaFeature
    header={
      <>
        <div className="flex flex-row">
          <span>Learn languages on the fly with </span>
        </div>
        <span className="text-purple-600">realistic text-to-speech</span>
        <br />
      </>
    }
    body={
      <>
        Creating cards for language learning has never been easier. Now you can
        insert audio, record audio, or generate speech for a given block of
        text.
        <br />
        <br />
        <em className="leading-loose">
          Zorbi supports over 24 languages and is powered by Amazon Alexa's
          technology.
        </em>
        <br />
        <br />
      </>
    }
    media={<TTSVideos />}
    mediaSide={"left"}
    disableMediaShadow={true}
  />
);

export const PricingCTA: React.FC<{
  currency: string | undefined;
  currencySymbol: string | undefined;
  weeklyPrice: string | number;
  loading: boolean;
}> = ({ currency, currencySymbol = "$", weeklyPrice, loading }) => (
  <section className="w-full py-8 bg-transparent">
    <div className="max-w-3xl mx-auto sm:px-10">
      <div className="relative flex flex-col overflow-hidden bg-gray-900 rounded-xl">
        <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-pink-500 to-purple-500"></div>
        <div className="flex flex-col items-center p-10 md:flex-row">
          <div className="w-full text-white md:w-2/3">
            {loading || !currency ? (
              <div
                role="status"
                className="flex flex-col max-w-sm space-y-2 animate-pulse"
              >
                <div className="h-16 bg-gray-800 rounded-lg dark:bg-gray-700 max-w-xs mb-2.5"></div>

                <span className="sr-only">Loading...</span>
              </div>
            ) : (
              <div className="flex items-center cros">
                <p className="flex items-start font-bold leading-none text-transparent text-7xl bg-clip-text bg-gradient-to-r from-purple-500 to-pink-500">
                  <>
                    <span className="mt-2 text-4xl">
                      {currencySymbol.toString()}
                    </span>
                    {weeklyPrice}
                  </>
                </p>
                <div className="flex flex-col ml-3">
                  <p className="text-base font-medium leading-tight text-gray-200">
                    {currency.toString()} per week
                  </p>
                  <p className="flex-shrink-0 text-xs text-gray-400">
                    Billed annually
                  </p>
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center w-full mt-8 md:w-1/3 md:justify-end md:mt-0">
            <OutboundLink
              href="https://zorbi.app/login?continue_path=/purchase/yearly?utm_source=landing_pricing"
              className="w-full px-10 py-5 text-xl font-semibold text-center text-white rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 md:w-auto"
            >
              Go Pro
            </OutboundLink>
          </div>
        </div>
        <div className="px-10 py-8 bg-gray-900 border-t border-gray-700 bg-gradient-to-b from-gray-800 to-gray-900">
          <h3 className="text-xl font-semibold leading-tight text-gray-200">
            Crafted For You With <span className="font-sans">❤</span>
          </h3>
          <p className="my-1 text-gray-400">
            Supercharge your learning and support Zorbi's mission to help a
            million learners around the world.
          </p>
          <p className="mt-3 text-gray-400">
            <OutboundLink
              href="/spaced-repetition-for-teachers"
              target="_blank"
            >
              <>
                Looking for a school license?{" "}
                <span className="underline">Learn more here.</span>
              </>
            </OutboundLink>
          </p>
        </div>
      </div>
    </div>
  </section>
);

const ZORBI_BENEFITS = [
  // Flashcards
  {
    benefit: "Spaced repetition flashcards",
    [PricingTier.Free]: true,
    [PricingTier.Pro]: true,
    [PricingTier.School]: true,
  },
  {
    benefit: "Integrate with Chrome and Notion",
    [PricingTier.Free]: true,
    [PricingTier.Pro]: true,
    [PricingTier.School]: true,
  },
  {
    benefit: "Add images, gifs, and audio to cards",
    [PricingTier.Free]: true,
    [PricingTier.Pro]: true,
    [PricingTier.School]: true,
  },
  {
    benefit: "Simple fill-in-the-blank cards",
    [PricingTier.Free]: true,
    [PricingTier.Pro]: true,
    [PricingTier.School]: true,
  },
  {
    benefit: "Study from any device anywhere",
    [PricingTier.Free]: true,
    [PricingTier.Pro]: true,
    [PricingTier.School]: true,
  },
  {
    benefit: "Share decks with others",
    [PricingTier.Free]: {
      text: "2 editor limit",
    },
    [PricingTier.Pro]: {
      text: "Unlimited",
    },
    [PricingTier.School]: {
      text: "Unlimited",
    },
  },
  // Limits

  {
    benefit: "Import from Anki, Quizlet, CSV",
    [PricingTier.Free]: {
      text: "50MB Limit",
    },
    [PricingTier.Pro]: {
      text: "100MB Limit",
    },
    [PricingTier.School]: {
      text: "Unlimited",
    },
  },

  // Language Specific
];

const LANGUAGE_BENEFITS = [
  {
    benefit: "Realistic text-to-speech",
    [PricingTier.Free]: false,
    [PricingTier.Pro]: true,
    [PricingTier.School]: true,
  },
];

const PRO_BENEFITS = [
  // Zorbi Pro
  {
    benefit: "Gradient colors for decks",
    [PricingTier.Free]: false,
    [PricingTier.Pro]: true,
    [PricingTier.School]: true,
  },
  {
    benefit: "Track learning progress",
    [PricingTier.Free]: false,
    [PricingTier.Pro]: true,
    [PricingTier.School]: true,
  },
  {
    benefit: "Annotate and hide areas of images",
    [PricingTier.Free]: false,
    [PricingTier.Pro]: true,
    [PricingTier.School]: true,
  },
  {
    benefit: "Exam date optimizer",
    [PricingTier.Free]: false,
    [PricingTier.Pro]: true,
    [PricingTier.School]: true,
  },
  {
    benefit: "Multi-group fill-in-the-blank cards (cloze)",
    [PricingTier.Free]: false,
    [PricingTier.Pro]: true,
    [PricingTier.School]: true,
  },

  {
    benefit: "Custom spaced-repetition profiles",
    [PricingTier.Free]: false,
    [PricingTier.Pro]: true,
    [PricingTier.School]: true,
  },

  {
    benefit: "Locked fields",
    [PricingTier.Free]: false,
    [PricingTier.Pro]: true,
    [PricingTier.School]: true,
    helpText:
      "Lets you lock the front or back of the card editor - useful for making multiple cards on the same concept.",
  },
];

const OTHER_BENEFITS = [
  // Community
  {
    benefit: "Priority support",
    [PricingTier.Free]: false,
    [PricingTier.Pro]: true,
    [PricingTier.School]: true,
  },
  {
    benefit: "Create and share decks to a class",
    [PricingTier.Free]: false,
    [PricingTier.Pro]: false,
    [PricingTier.School]: true,
  },
  {
    benefit: "Set homework in Zorbi",
    [PricingTier.Free]: false,
    [PricingTier.Pro]: false,
    [PricingTier.School]: true,
  },
  {
    benefit: "Monitor student knowledge retention",
    [PricingTier.Free]: false,
    [PricingTier.Pro]: false,
    [PricingTier.School]: true,
  },
];

const SimpleBenefitsList = {
  [PricingTier.Free]: ["Study unlimited flashcards", "Spaced repetition"],

  [PricingTier.Pro]: [
    "Study unlimited flashcards",
    "Spaced repetition",
    "Smart algorithm optimizer",
    "Flashcards from diagrams",
    "Realistic text-to-speech",
    "Increased upload limits",
  ],
  [PricingTier.School]: [
    "Study unlimited flashcards",
    "Spaced repetition",
    "Pro features for every student",
    "Knowledge retention insights",
    "LMS Integrations",
    "Set homework from Zorbi",
  ],
};

export const PricingBenefitBox: React.FC<{
  data: PricingTierData;
  mode: PricingMode;
}> = ({ data, mode }) => {
  const priceData = data[mode];

  return (
    <div className="w-full max-w-md px-0 mx-auto mb-6 lg:w-1/3 lg:px-3 lg:mb-0">
      <div
        className={cx(
          `relative flex flex-col h-full mb-10 bg-white border-2  rounded-lg shadow-sm sm:mb-0`,
          {
            "border-gray-200": data.tier != PricingTier.Pro,
            "shadow-sm ring-4 ring-opacity-25 ring-purple-300 ":
              data.tier == PricingTier.Pro,
          }
        )}
      >
        {data.tier == PricingTier.Pro && (
          <span className="absolute flex items-center justify-center w-full py-2 text-sm font-semibold text-white rounded-t-lg bg-gradient-to-r from-blue-600 via-purple-500 to-red-400">
            Early Bird Price
          </span>
        )}
        <div className="px-10 pt-12">
          <span className="px-4 py-1 text-base font-semibold text-purple-700 bg-purple-100 rounded-full text-uppercase">
            {data.label}
          </span>
        </div>

        <div className="px-10 mt-5">
          <span className="text-5xl font-bold">${priceData.price}</span>
          <span className="text-xl font-bold text-gray-500">
            {priceData.cadence}
          </span>
        </div>

        <div className="px-10 pb-10 mt-3">
          <p className="text-lg leading-7 text-gray-500">{data.text}</p>
        </div>

        <div className="flex flex-col h-full p-10 pb-0 bg-gray-100 rounded-b-lg">
          <ul className="flex flex-col h-full space-y-4">
            {SimpleBenefitsList[data.tier].map((val) => {
              return (
                <li>
                  <span className="flex items-center text-green-500">
                    <svg
                      className="w-5 h-5 text-green-400"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      ></path>
                    </svg>

                    <span className="ml-1 font-semibold tracking-wide text-gray-700">
                      {val}
                    </span>
                  </span>
                </li>
              );
            })}
          </ul>

          <div className="my-8">
            <a
              href={priceData.link}
              target="_blank noreferrer"
              className="inline-flex items-center justify-center w-full px-4 py-3 text-xs font-semibold tracking-widest text-white uppercase transition duration-150 ease-in-out bg-gray-800 border border-transparent rounded-md hover:bg-gray-700 active:bg-gray-900 focus:outline-none focus:border-gray-900 focus:shadow-outline-gray disabled:opacity-25"
            >
              {data.ctaText}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export const DetailedPricingCTA: React.FC = () => {
  const [mode, setMode] = useState<PricingMode>(PricingMode.Monthly);

  return (
    <section className="flex flex-col items-center justify-center w-full mb-8 bg-transparent">
      <div className="flex flex-col items-center justify-center mb-8 md:mb-16 lg:mb-20">
        <nav className="relative inline-flex overflow-hidden text-sm border border-gray-300 rounded-lg">
          <div
            className={cx(
              "absolute w-1/2 h-full transition-all duration-150 ease-out bg-gradient-to-br from-purple-600 to-purple-700",
              {
                "left-0": mode == PricingMode.Monthly,
                "left-1/2": mode == PricingMode.Annual,
                //"left-2/3": mode == PricingMode.Lifetime,
              }
            )}
          ></div>
          {Object.entries(PRO_PRICING_PLANS).map((entry) => {
            const curMode = Number(entry[0]) as PricingMode;

            return (
              <button
                onClick={() => setMode(curMode)}
                className={cx(
                  "relative z-20 px-5 py-3 font-bold focus:outline-none",
                  {
                    "text-white": mode == curMode,
                    "text-gray-600 hover:text-gray-700": mode != curMode,
                  }
                )}
              >
                {entry[1].label} Plan
              </button>
            );
          })}
        </nav>
      </div>
      <div className="flex flex-col w-full space-y-4 md:max-w-6xl lg:flex-row lg:space-y-0 lg:space-x-4">
        <PricingBenefitBox data={PRICING_TIERS[PricingTier.Free]} mode={mode} />

        <PricingBenefitBox data={PRICING_TIERS[PricingTier.Pro]} mode={mode} />

        <PricingBenefitBox
          data={PRICING_TIERS[PricingTier.School]}
          mode={mode}
        />
      </div>
    </section>
  );
};

const FeatureTable: React.FC<{ plans: any; benefits: any }> = ({
  plans,
  benefits,
}) => {
  return (
    <div className="relative">
      {/* Fake card backgrounds */}
      <div
        className="absolute inset-0 flex items-stretch pointer-events-none"
        aria-hidden="true"
      >
        <div className="w-1/4 pr-4" />
        <div className="w-1/4 px-4">
          <div className="w-full h-full bg-white rounded-lg shadow" />
        </div>
        <div className="w-1/4 px-4">
          <div className="w-full h-full bg-white rounded-lg shadow-md" />
        </div>
        <div className="w-1/4 pl-4">
          <div className="w-full h-full bg-white rounded-lg shadow" />
        </div>
      </div>

      <table className="relative w-full">
        <thead>
          <tr className="text-left">
            <th scope="col">
              <span className="sr-only">Feature</span>
            </th>
            {plans.map((plan) => (
              <th key={plan.label} scope="col">
                <span className="sr-only">{plan.label} plan</span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {benefits.map((feature) => (
            <tr key={feature.benefit}>
              <th
                scope="row"
                className="w-1/4 py-3 pr-4 text-sm font-semibold text-left text-gray-600"
              >
                {feature.benefit}
              </th>
              {plans.map(({ tier, ...plan }, tierIdx) => {
                const featureData =
                  typeof feature[tier] == "boolean"
                    ? {
                        icon: feature[tier] ? "checked" : "cross",
                        text: feature.benefit,
                      }
                    : (feature[tier] as any);
                return (
                  <td
                    key={tier + feature.benefit}
                    className={cx(
                      tierIdx === plans.length - 1 ? "pl-4" : "px-4",
                      "relative w-1/4 py-0 text-center"
                    )}
                  >
                    <span className="relative w-full h-full py-3">
                      {typeof feature[tier] != "boolean" ? (
                        <span
                          className={cx(
                            plan.featured ? "text-indigo-600" : "text-gray-900",
                            "text-sm font-medium"
                          )}
                        >
                          {featureData.text}
                        </span>
                      ) : (
                        <>
                          {featureData.icon == "checked" ? (
                            <CheckIcon
                              className="w-5 h-5 mx-auto text-indigo-600"
                              aria-hidden="true"
                            />
                          ) : (
                            <XIcon
                              className="w-5 h-5 mx-auto text-gray-400"
                              aria-hidden="true"
                            />
                          )}
                        </>
                      )}
                    </span>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>

      {/* Fake card borders */}
      <div
        className="absolute inset-0 flex items-stretch pointer-events-none"
        aria-hidden="true"
      >
        <div className="w-1/4 pr-4" />
        <div className="w-1/4 px-4">
          <div className="w-full h-full rounded-lg ring-1 ring-black ring-opacity-5" />
        </div>
        <div className="w-1/4 px-4">
          <div className="w-full h-full rounded-lg ring-2 ring-indigo-600 ring-opacity-100" />
        </div>
        <div className="w-1/4 pl-4">
          <div className="w-full h-full rounded-lg ring-1 ring-black ring-opacity-5" />
        </div>
      </div>
    </div>
  );
};

const DetailedFeatureTable: React.FC = () => {
  const plans = Object.values(PRICING_TIERS);
  return (
    <section aria-labelledby="comparison-heading" className="hidden lg:block">
      <h2 id="comparison-heading" className="sr-only">
        Feature comparison
      </h2>

      <div className="px-8 py-24 mx-auto max-w-7xl">
        <div className="flex items-stretch w-full border-t border-gray-200">
          <div className="flex items-end w-1/4 py-6 pr-4 -mt-px">
            <h3 className="mt-auto text-sm font-bold text-gray-900">
              Features
            </h3>
          </div>
          {Object.values(PRICING_TIERS).map((plan, planIdx) => (
            <div
              key={plan.label}
              aria-hidden="true"
              className={cx(
                planIdx === Object.values(PRICING_TIERS).length - 1
                  ? ""
                  : "pr-4",
                "-mt-px pl-4 w-1/4"
              )}
            >
              <div
                className={cx(
                  plan.featured ? "border-indigo-600" : "border-transparent",
                  "py-6 border-t-2"
                )}
              >
                <p
                  className={cx(
                    plan.featured ? "text-indigo-600" : "text-gray-900",
                    "text-sm font-bold"
                  )}
                >
                  {plan.label}
                </p>
              </div>
            </div>
          ))}
        </div>

        <FeatureTable plans={plans} benefits={ZORBI_BENEFITS} />

        <h3 className="mt-10 text-sm font-bold text-gray-900">Pro Benefits</h3>

        <FeatureTable
          plans={plans}
          benefits={PRO_BENEFITS.concat(LANGUAGE_BENEFITS)}
        />

        <h3 className="mt-10 text-sm font-bold text-gray-900">Other</h3>
        <FeatureTable plans={plans} benefits={OTHER_BENEFITS} />
      </div>
    </section>
  );
};

const MobileFeatureTable: React.FC<{ plan: any; benefits: any }> = ({
  plan,
  benefits,
}) => {
  return (
    <div className="relative mt-6">
      {/* Fake card background */}
      <div
        aria-hidden="true"
        className="absolute inset-0 hidden pointer-events-none sm:block"
      >
        <div
          className={cx(
            plan.featured ? "shadow-md" : "shadow",
            "absolute right-0 w-1/2 h-full bg-white rounded-lg"
          )}
        />
      </div>

      <div
        className={cx(
          plan.featured
            ? "ring-2 ring-purple-600 shadow-md"
            : "ring-1 ring-black ring-opacity-5 shadow",
          "relative py-3 px-4 bg-white rounded-lg sm:p-0 sm:bg-transparent sm:rounded-none sm:ring-0 sm:shadow-none"
        )}
      >
        <dl className="divide-y divide-gray-200">
          {benefits.map((feature) => {
            const featureData =
              typeof feature[plan.tier] == "boolean"
                ? {
                    icon: feature[plan.tier] ? "checked" : "cross",
                    text: feature.benefit,
                  }
                : (feature[plan.tier] as any);
            return (
              <div
                key={feature.benefit}
                className="flex items-center justify-between py-3 sm:grid sm:grid-cols-2"
              >
                <dt className="pr-4 text-sm font-medium text-gray-600">
                  {feature.benefit}
                </dt>
                <dd className="flex items-center justify-end sm:px-4 sm:justify-center">
                  {typeof feature[plan.tier] != "boolean" ? (
                    <span
                      className={cx(
                        plan.featured ? "text-purple-600" : "text-gray-900",
                        "text-sm font-medium"
                      )}
                    >
                      {featureData?.text}
                    </span>
                  ) : (
                    <>
                      {featureData?.icon == "checked" ? (
                        <CheckIcon
                          className="w-5 h-5 mx-auto text-purple-600"
                          aria-hidden="true"
                        />
                      ) : (
                        <XIcon
                          className="w-5 h-5 mx-auto text-gray-400"
                          aria-hidden="true"
                        />
                      )}
                    </>
                  )}
                </dd>
              </div>
            );
          })}
        </dl>
      </div>

      {/* Fake card border */}
      <div
        aria-hidden="true"
        className="absolute inset-0 hidden pointer-events-none sm:block"
      >
        <div
          className={cx(
            plan.featured
              ? "ring-2 ring-purple-600"
              : "ring-1 ring-black ring-opacity-5",
            "absolute right-0 w-1/2 h-full rounded-lg"
          )}
        />
      </div>
    </div>
  );
};
export const DeatiledMobileFeatureTable: React.FC<any> = ({}) => {
  return (
    <section aria-labelledby="mobile-comparison-heading" className="lg:hidden">
      <h2 id="mobile-comparison-heading" className="sr-only">
        Feature comparison
      </h2>

      <div className="max-w-2xl px-4 py-16 mx-auto space-y-16 sm:px-6">
        {Object.values(PRICING_TIERS).map((plan, mobilePlanIndex) => (
          <div key="plan.title" className="border-t border-gray-200">
            <div
              className={cx(
                plan.featured ? "border-purple-600" : "border-transparent",
                "-mt-px pt-6 border-t-2 sm:w-1/2"
              )}
            >
              <h3
                className={cx(
                  plan.featured ? "text-purple-600" : "text-gray-900",
                  "text-sm font-bold"
                )}
              >
                {plan.label}
              </h3>
              <p className="mt-2 text-sm text-gray-500">{plan.text}</p>
            </div>
            <h4 className="mt-10 text-sm font-bold text-gray-900">
              Base Features
            </h4>

            <MobileFeatureTable plan={plan} benefits={ZORBI_BENEFITS} />
            <h4 className="mt-10 text-sm font-bold text-gray-900">
              Pro Benefits
            </h4>

            <MobileFeatureTable
              plan={plan}
              benefits={PRO_BENEFITS.concat(LANGUAGE_BENEFITS)}
            />

            <h4 className="mt-10 text-sm font-bold text-gray-900">Other</h4>

            <MobileFeatureTable plan={plan} benefits={OTHER_BENEFITS} />

            {/* Fake card border */}
            <div
              aria-hidden="true"
              className="absolute inset-0 hidden pointer-events-none sm:block"
            >
              <div className={cx("absolute right-0 w-1/2 h-full rounded-lg")} />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export const Pricing: React.FC = () => {
  const { data, loading } = useMembershipPrice();

  return (
    <section className="py-20 bg-gray-50">
      <div className="w-full px-8 mx-auto lg:px-4">
        <PricingHero />
        <PricingCTA
          currencySymbol={data?.currencySymbol}
          currency={data?.currency}
          weeklyPrice={data?.weekly}
          loading={loading}
        />

        {/* <DetailedPricingCTA /> */}
        {/* <PricingCTA /> */}
        <ImageClozeFeature />
        {/*Smart learning feature*/}
        {/*Audio, text-to-speech, and more*/}
        <SmartLearningFeature />

        <TTSFeature />
        <DetailedFeatureTable />

        <DeatiledMobileFeatureTable />
      </div>
    </section>
  );
};
